import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Icon, LegalDocumentDialog } from '@carrent/ui'
import { useAuthStore, isTelemetryEnabled, setTelemetryEnabled } from '@carrent/core'
import { authApi } from '@carrent/api'
import { useLegal, fetchLegalDocument } from '@carrent/domain'
import { MORE_ITEMS } from '../../layout/navItems'

export default function MorePage() {
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const [telemetry, setTelemetry] = useState(isTelemetryEnabled)
  const { legal } = useLegal()

  // Открытый правовой документ: { key, title, text, loading, error }.
  const [doc, setDoc] = useState(null)

  const openDoc = async (key, fallbackTitle) => {
    setDoc({ key, title: fallbackTitle, text: '', loading: true, error: '' })
    try {
      const data = await fetchLegalDocument(key)
      setDoc({
        key,
        title: data?.title || fallbackTitle,
        text: data?.text || '',
        loading: false,
        error: '',
      })
    } catch {
      setDoc({
        key,
        title: fallbackTitle,
        text: '',
        loading: false,
        error: 'Не удалось загрузить документ',
      })
    }
  }

  const toggleTelemetry = () => {
    setTelemetry(setTelemetryEnabled(!telemetry))
  }

  const handleLogout = async () => {
    try {
      await authApi.logout()
    } catch {
      // Выход не должен зависеть от доступности сети.
    }
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className="p-page flex-col gap">
      <h1 className="page-title">Ещё</h1>

      {user && (
        <div className="card card--flat">
          <div className="more-user__name">{user.full_name || user.phone}</div>
          <div className="text-muted">{user.phone}</div>
        </div>
      )}

      <nav className="more-list" aria-label="Дополнительные разделы">
        {MORE_ITEMS.map((item) => (
          <Link key={item.to} to={item.to} className="more-list__item">
            <Icon name={item.icon} size={22} className="more-list__icon" />
            <span className="more-list__text">
              <span className="more-list__label">{item.label}</span>
              <span className="more-list__hint">{item.hint}</span>
            </span>
            <Icon name="chevronRight" size={18} className="more-list__chevron" />
          </Link>
        ))}
      </nav>

      <section className="settings">
        <h2 className="section-title">Настройки</h2>

        <label className="settings__row">
          <span className="settings__text">
            <span className="settings__label">Помогать улучшать приложение</span>
            <span className="settings__hint">
              Отправлять обезличенные данные о том, какие экраны открываются
              и где возникают ошибки. Введённый текст и личные данные
              не передаются.
            </span>
          </span>
          <input
            type="checkbox"
            className="switch"
            checked={telemetry}
            onChange={toggleTelemetry}
          />
        </label>
      </section>

      {legal?.enabled !== false && (
        <section className="settings">
          <h2 className="section-title">Документы</h2>

          {/* Согласие даётся при регистрации, но документы должны
              оставаться доступными и после неё: иначе прочитать,
              с чем согласился, можно только до входа. Текст открывается
              внутри приложения — документ хранится в настройках,
              внешней страницы больше нет. */}
          <nav className="more-list" aria-label="Правовые документы">
            <button
              type="button"
              className="more-list__item"
              onClick={() => openDoc('user_agreement', legal.user_agreement_title)}
            >
              <Icon name="file" size={22} className="more-list__icon" />
              <span className="more-list__text">
                <span className="more-list__label">{legal.user_agreement_title}</span>
                <span className="more-list__hint">Условия использования сервиса</span>
              </span>
              <Icon name="chevronRight" size={16} className="more-list__chevron" />
            </button>

            <button
              type="button"
              className="more-list__item"
              onClick={() => openDoc('privacy_policy', legal.privacy_policy_title)}
            >
              <Icon name="file" size={22} className="more-list__icon" />
              <span className="more-list__text">
                <span className="more-list__label">{legal.privacy_policy_title}</span>
                <span className="more-list__hint">Как обрабатываются ваши данные</span>
              </span>
              <Icon name="chevronRight" size={16} className="more-list__chevron" />
            </button>
          </nav>
        </section>
      )}

      <LegalDocumentDialog
        open={doc !== null}
        title={doc?.title}
        text={doc?.text}
        loading={doc?.loading}
        error={doc?.error}
        onClose={() => setDoc(null)}
        onRetry={() => doc && openDoc(doc.key, doc.title)}
      />

      <button type="button" className="btn btn--secondary btn--full" onClick={handleLogout}>
        <Icon name="logout" size={18} />
        Выйти
      </button>
    </div>
  )
}
